import { ArrowRight } from "lucide-react";
import type { MouseEvent, ReactNode } from "react";

export interface WidgetAction {
  label: string;
  /** Lien cible. Utiliser `"#"` comme placeholder tant que la route n'existe
   *  pas — le composant empêchera le scroll au top via preventDefault. */
  href: string;
  /** Callback optionnel (utile pour navigation programmatique React Router
   *  via `useNavigate()` quand on ne veut pas full-reload). */
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
}

export interface WidgetHeaderProps {
  /** Surtitre mono majuscule (ex : "Pilotage · Vue générale"). */
  eyebrow: string;
  /** Titre du widget (ex : "Mon stock vivant"). */
  title: string;
  /** Élément optionnel rendu inline à côté du titre (ex : bulle d'aide « ? »). */
  titleHelp?: ReactNode;
  /** Lien optionnel à droite (ex : "Voir le détail →"). */
  action?: WidgetAction;
  /** Slot pour remplacer entièrement la zone de droite (compteur, badge…). */
  rightSlot?: ReactNode;
}

/**
 * En-tête de widget standard : eyebrow + titre à gauche, lien ou slot à droite.
 * Utilisé par tous les widgets de dashboard, de fiche détail, etc.
 */
export function WidgetHeader({ eyebrow, title, titleHelp, action, rightSlot }: WidgetHeaderProps) {
  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    // Empêche le scroll au top quand l'href est un placeholder
    if (action?.href === "#") {
      event.preventDefault();
    }
    action?.onClick?.(event);
  };

  return (
    <header className="mb-5 flex flex-wrap items-start justify-between gap-2">
      <div className="min-w-0 flex-1">
        <div className="eyebrow">{eyebrow}</div>
        {/* Slot d'aide optionnel : titre + bulle « ? » alignés (sinon markup inchangé). */}
        {titleHelp ? (
          <div className="mt-1 flex items-center gap-1">
            <h3 className="text-[18px] font-semibold">{title}</h3>
            {titleHelp}
          </div>
        ) : (
          <h3 className="mt-1 text-[18px] font-semibold">{title}</h3>
        )}
      </div>

      {rightSlot ??
        (action && (
          <a
            href={action.href}
            onClick={handleClick}
            className="inline-flex items-center gap-1 text-[13px] font-medium text-champagne-700 transition-colors hover:text-champagne-900"
          >
            {action.label} <ArrowRight size={14} />
          </a>
        ))}
    </header>
  );
}
